<template>
  <div class="date-picker">
    <input
      ref="input"
      type="date"
      class="form-control"
      :class="{ 'is-invalid': hasError }"
      :value="formattedValue"
      :min="minDate"
      :max="maxDate"
      :disabled="disabled"
      :readonly="readonly"
      @input="handleInput"
      @focus="$emit('focus', $event)"
      @blur="$emit('blur', $event)"
    />
    <div v-if="hasError && errorMessage" class="error-message">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'DatePicker',
  props: {
    value: {
      type: [String, Date],
      default: '',
    },
    minDate: {
      type: [String, Date],
      default: null,
    },
    maxDate: {
      type: [String, Date],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: '',
    },
  },
  computed: {
    formattedValue() {
      if (!this.value) return '';

      const date = new Date(this.value);
      if (Number.isNaN(date.getTime())) return '';

      // Format as YYYY-MM-DD for input[type="date"]
      return date.toISOString().split('T')[0];
    },
  },
  methods: {
    handleInput(event) {
      const inputValue = event.target.value;

      if (!inputValue) {
        this.$emit('input', '');
        this.$emit('change', '');
        return;
      }

      const date = new Date(inputValue);

      if (Number.isNaN(date.getTime())) {
        this.$emit('error', 'Invalid date format');
        return;
      }

      // Validate against min/max dates
      if (this.minDate) {
        const minDate = new Date(this.minDate);
        if (date < minDate) {
          this.$emit('error', `Date cannot be earlier than ${this.formatDisplayDate(minDate)}`);
          return;
        }
      }

      if (this.maxDate) {
        const maxDate = new Date(this.maxDate);
        if (date > maxDate) {
          this.$emit('error', `Date cannot be later than ${this.formatDisplayDate(maxDate)}`);
          return;
        }
      }

      this.$emit('input', inputValue);
      this.$emit('change', inputValue);
    },

    formatDisplayDate(date) {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },

    focus() {
      this.$refs.input.focus();
    },

    blur() {
      this.$refs.input.blur();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../scss/variable.scss';

.date-picker {
  width: 100%;
}

.form-control {
  &::-webkit-calendar-picker-indicator {
    color: $primary;
    opacity: 1;
  }
}

.error-message {
  color: $primary;
  font-size: 12px;
  margin-top: 4px;
}
</style>
